/**
 * 流程节点表单权限配置：可见/可编辑 ACL + 默认值绑定
 *
 * 存储形态（新）：
 *   { __v: 2, acl: { [fullkey]: 0|1|2 }, binds: { [fullkey]: 'executor'|'created_at'|'clone_value' } }
 * 旧形态：
 *   { [fullkey]: 0|1|2 }
 *
 * ACL 语义：缺省/0 可见可编辑；1 可见只读；2 隐藏
 * 默认值绑定在节点执行后写入 formData，与字段是否可见无关
 */

export const BIND_EXECUTOR = 'executor';
export const BIND_CREATED_AT = 'created_at';
export const BIND_CLONE_VALUE = 'clone_value';
export const BIND_OPINION = 'opinion';
export const BIND_AGREE = 'agree';

export const BIND_OPTIONS = [
  {value: '', label: '无'},
  {value: BIND_EXECUTOR, label: '操作人'},
  {value: BIND_CREATED_AT, label: '操作时间'},
  {value: BIND_CLONE_VALUE, label: '克隆-对象'},
  {value: BIND_OPINION, label: '审批意见'},
  {value: BIND_AGREE, label: '是否同意'},
];

/** 默认值下拉选项；cloneName 有值时展示「克隆-部门」这类文案 */
export function getBindOptions({hasClone = false, cloneName = '', hasOpinion = false} = {}) {
  const opts = [
    {value: '', label: '无'},
    {value: BIND_EXECUTOR, label: '操作人'},
    {value: BIND_CREATED_AT, label: '操作时间'},
  ];
  if (hasClone) {
    const name = cloneName || '对象';
    opts.push({value: BIND_CLONE_VALUE, label: `克隆-${name}`});
  }
  if (hasOpinion) {
    opts.push({value: BIND_OPINION, label: '审批意见'});
    opts.push({value: BIND_AGREE, label: '是否同意'});
  }
  return opts;
}

/**
 * 审批意见模式：必须 / 可选 / 表单
 * - 必须/可选：右侧展示意见填写与附件
 * - 表单：意见在表单内填写，右侧提交（非弱审批时含同意/退回）
 * - 弱审批：只提意见，右侧不出现同意/退回，提交后默认前进
 * 兼容旧值「无」→「表单」；旧字段 enableOpinion（true→可选，false→表单）
 */
export function resolveOpinionMode(opt = {}) {
  const mode = opt.opinionMode;
  if (mode === '无') return '表单';
  if (mode === '必须' || mode === '可选' || mode === '表单') {
    return mode;
  }
  if (opt.enableOpinion === false) return '表单';
  if (opt.enableOpinion === true) return '可选';
  return '可选';
}

/** 右侧需要意见文本/附件面板（必须/可选） */
export function isOpinionEnabled(opt = {}) {
  const mode = resolveOpinionMode(opt);
  return mode === '必须' || mode === '可选';
}

/** 表单内填写意见：右侧不展示意见输入与上传 */
export function isOpinionFormMode(opt = {}) {
  return resolveOpinionMode(opt) === '表单';
}

/** 表单权限中可绑定意见/是否同意（必须/可选/表单均可） */
export function isOpinionBindable(opt = {}) {
  const mode = resolveOpinionMode(opt);
  return mode === '必须' || mode === '可选' || mode === '表单';
}

export function isOpinionRequired(opt = {}) {
  return resolveOpinionMode(opt) === '必须';
}

export function isPackedAcl(value) {
  return !!(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value.__v === 2 ||
      (value.binds && typeof value.binds === 'object') ||
      (value.acl && typeof value.acl === 'object' && !Array.isArray(value.acl) &&
        Object.keys(value).some((k) => k === 'acl' || k === 'binds' || k === '__v')))
  );
}

/** 拆出扁平 ACL map（供表单渲染） */
export function unpackAclMap(value) {
  if (!value || typeof value !== 'object') return {};
  if (isPackedAcl(value)) {
    const acl = value.acl && typeof value.acl === 'object' ? value.acl : {};
    const out = {};
    Object.keys(acl).forEach((k) => {
      const n = Number(acl[k]);
      if (!Number.isNaN(n) && n > 0) out[k] = n;
    });
    return out;
  }
  const out = {};
  Object.keys(value).forEach((k) => {
    if (k === 'acl' || k === 'binds' || k === '__v') return;
    const n = Number(value[k]);
    if (!Number.isNaN(n) && n > 0) out[k] = n;
  });
  return out;
}

/** 拆出值绑定 map */
export function unpackBindsMap(value) {
  if (!value || typeof value !== 'object') return {};
  if (isPackedAcl(value) && value.binds && typeof value.binds === 'object') {
    const out = {};
    Object.keys(value.binds).forEach((k) => {
      const b = value.binds[k];
      if (b) out[k] = String(b);
    });
    return out;
  }
  return {};
}

export function packAclValue(aclMap, bindsMap) {
  const acl = {};
  Object.keys(aclMap || {}).forEach((k) => {
    const n = Number(aclMap[k]);
    if (!Number.isNaN(n) && n > 0) acl[k] = n;
  });
  const binds = {};
  Object.keys(bindsMap || {}).forEach((k) => {
    if (bindsMap[k]) binds[k] = String(bindsMap[k]);
  });
  return {__v: 2, acl, binds};
}

export function getByPath(obj, path) {
  if (obj == null || !path) return undefined;
  const parts = String(path).split('.').filter(Boolean);
  let cur = obj;
  for (let i = 0; i < parts.length; i++) {
    if (cur == null) return undefined;
    cur = cur[parts[i]];
  }
  return cur;
}

export function setByPath(obj, path, value) {
  if (!obj || !path) return obj;
  const parts = String(path).split('.').filter(Boolean);
  if (!parts.length) return obj;
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (cur[p] == null || typeof cur[p] !== 'object') {
      cur[p] = {};
    }
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return obj;
}

const ACL_LAYOUT_CONTROLS = new Set([
  'UgoRow', 'UgoSection', 'UgoTabs', 'UgoCol', 'UgoTab',
  'Row', 'Section', 'Tabs', 'Col', 'Tab',
]);

const ACL_NESTED_CONTROLS = new Set([
  'UgoInnerForm', 'UgoMultiForm',
  'InnerForm', 'MultiForm',
]);

function isAclLayoutControl(control) {
  return ACL_LAYOUT_CONTROLS.has(String(control || ''));
}

function isAclNestedControl(control) {
  return ACL_NESTED_CONTROLS.has(String(control || ''));
}

/**
 * 为表单字段写入与 PermissionSelect 一致的 fullkey，保证运行时 acl[fullkey] 能命中
 */
export function applyFormFieldFullkeys(fields, parentPath = '') {
  (fields || []).forEach((field, index) => {
    if (!field) return;
    const control = String(field.control || '');
    const key = field.key || field.name;
    const layout = isAclLayoutControl(control);
    const skipPath = layout;
    const selfPath = (!key || skipPath) ?
      parentPath :
      (parentPath ? `${parentPath}.${key}` : String(key));
    if (key && !skipPath) {
      field.fullkey = selfPath;
    } else {
      // 布局节点：固定用 id（与 PermissionSelect 一致），便于整块隐藏
      field.fullkey = field.id ?
        String(field.id) :
        `${parentPath || 'root'}__${control || 'node'}__${index}`;
    }
    const childPath = skipPath ? parentPath : selfPath;
    if (Array.isArray(field.fields) && field.fields.length) {
      applyFormFieldFullkeys(field.fields, childPath);
    }
  });
  return fields;
}

/**
 * 把 parent.child 形式的 ACL 拆进 InnerForm/MultiForm.config.acl（相对路径）
 */
export function injectNestedAclIntoFields(fields, acl, parentPath = '') {
  const map = acl || {};
  (fields || []).forEach((field) => {
    if (!field) return;
    const control = String(field.control || '');
    const key = field.key || field.name;
    const layout = isAclLayoutControl(control);
    const nested = isAclNestedControl(control);
    const skipPath = layout;
    const selfPath = (!key || skipPath) ?
      parentPath :
      (parentPath ? `${parentPath}.${key}` : String(key));
    const childPath = skipPath ? parentPath : selfPath;

    if (nested && key) {
      const prefix = selfPath;
      const nestedAcl = {};
      Object.keys(map).forEach((k) => {
        if (k === prefix) return;
        if (k.indexOf(prefix + '.') === 0) {
          nestedAcl[k.slice(prefix.length + 1)] = map[k];
        }
      });
      field.config = {...(field.config || {}), acl: nestedAcl};
    }
    if (Array.isArray(field.fields) && field.fields.length) {
      injectNestedAclIntoFields(field.fields, map, childPath);
    }
  });
  return fields;
}

/** 运行时表单：对齐 fullkey + 注入内嵌表 ACL */
export function prepareFormForAcl(form, aclMap) {
  if (!form || !Array.isArray(form.fields)) return form;
  applyFormFieldFullkeys(form.fields);
  injectNestedAclIntoFields(form.fields, aclMap || {});
  return form;
}
